import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form, FormItem, Input } from '@packages/utils';
import { Select } from 'components/Base/Select';
import { useUpdateCustomTacticMutation } from 'redux/apiSlice/pillarsActionPlan.slice';
import { useGetPillarsOfStrengthQuery } from 'redux/apiSlice/pillars.slice';
import { TUpdateCustomTacticRequest } from 'data/types/pillarsActionPlan.types';
import useForm from 'hooks/useForm';
import {
  useShowToastNotification,
  ENotificationType,
} from 'components/Base/Notification';
import {
  StyledModal,
  ModalFooter,
  CancelButton,
  PrimaryButton,
  FormContainer,
} from 'components/Shared/StyledModal';
import { GoalLevelFields } from '../GoalLevelFields';
import {
  TEditTacticModalProps,
  TEditTacticFormValues,
} from './EditTacticModal.types';

const EditTacticModal: React.FC<TEditTacticModalProps> = ({
  open,
  onClose,
  onEdit,
  tactic,
  elcId,
}) => {
  const { form } = useForm<TEditTacticFormValues>();
  const { showToastNotification } = useShowToastNotification();
  const [updateCustomTactic, { isLoading }] = useUpdateCustomTacticMutation();
  const [tacticNameError, setTacticNameError] = useState<string | null>(null);

  const [selectedPillarId, setSelectedPillarId] = useState<
    string | undefined
  >();
  const [selectedSubPillarId, setSelectedSubPillarId] = useState<
    string | undefined
  >();

  // The BE tolerates a blank elcId on this endpoint (returns the tree with no
  // per-ELC statuses), so the All-ELCs page still gets the hierarchy needed
  // to power the cascade.
  const {
    data: pillarsOfStrength,
    isError: pillarsOfStrengthError,
    error: pillarsOfStrengthErrorPayload,
  } = useGetPillarsOfStrengthQuery({ elcId: elcId ?? '' }, { skip: !open });

  const pillarsErrorToastShown = useRef(false);

  useEffect(() => {
    if (!open) {
      pillarsErrorToastShown.current = false;
      return;
    }
    if (pillarsOfStrengthError && !pillarsErrorToastShown.current) {
      pillarsErrorToastShown.current = true;
      const message =
        (pillarsOfStrengthErrorPayload as any)?.data?.message ||
        'Unable to load Pillar, Goal, and Level options. Please try again or contact support.';
      showToastNotification({
        type: ENotificationType.ERROR,
        message,
      });
    }
  }, [
    open,
    pillarsOfStrengthError,
    pillarsOfStrengthErrorPayload,
    showToastNotification,
  ]);

  const pillarsOptions = useMemo(
    () =>
      pillarsOfStrength?.pillars.map(p => ({ label: p.name, value: p.id })) ??
      [],
    [pillarsOfStrength],
  );

  const handlePillarChange = () => {
    form.setFieldsValue({ subPillarId: undefined, levelId: undefined });
    setSelectedSubPillarId(undefined);
  };

  const handleSubPillarChange = () => {
    form.setFieldsValue({ levelId: undefined });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!tactic?.actionPlanId) {
        throw new Error('Missing action plan ID for tactic update');
      }

      const updatePayload: TUpdateCustomTacticRequest = {
        actionPlanId: tactic.actionPlanId,
        title: values.tacticName.trim(),
        pillarsId: values.pillarId,
      };
      if (values.subPillarId) updatePayload.subPillarId = values.subPillarId;
      if (values.levelId) updatePayload.levelId = values.levelId;

      await updateCustomTactic(updatePayload).unwrap();

      showToastNotification({
        type: ENotificationType.SUCCESS,
        message: `Tactic "${values.tacticName}" updated successfully!`,
      });

      await onEdit(values);
    } catch (error: any) {
      if (
        error?.status === 400 &&
        error?.data?.message?.includes('already exist')
      ) {
        const errorMessage =
          'This tactic title already exists. Please choose a different name.';

        setTacticNameError(errorMessage);
        form.setFields([{ name: 'tacticName', errors: [errorMessage] }]);
        showToastNotification({
          type: ENotificationType.ERROR,
          message: errorMessage,
        });
        return;
      }

      if (error?.status === 400) {
        showToastNotification({
          type: ENotificationType.ERROR,
          message:
            error?.data?.message ||
            'Invalid data provided. Please check your inputs.',
        });
        return;
      }

      if (error?.status === 403) {
        showToastNotification({
          type: ENotificationType.ERROR,
          message: 'You do not have permission to edit this tactic.',
        });
        return;
      }

      if (error?.status === 404) {
        showToastNotification({
          type: ENotificationType.ERROR,
          message: 'Tactic not found. It may have been deleted.',
        });
        return;
      }

      showToastNotification({
        type: ENotificationType.ERROR,
        message:
          error?.data?.message || 'Failed to update tactic. Please try again.',
      });
      throw error;
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setTacticNameError(null);
    setSelectedPillarId(undefined);
    setSelectedSubPillarId(undefined);
    onClose();
  };

  const handleTacticNameChange = () => {
    if (tacticNameError) {
      setTacticNameError(null);
      form.setFields([{ name: 'tacticName', errors: [] }]);
    }
  };

  useEffect(() => {
    if (open && tactic) {
      form.setFieldsValue({
        tacticName: tactic.tacticTitle,
        pillarId: tactic.pillarId,
        subPillarId: tactic.goalId,
        levelId: tactic.levelId,
      });
      setSelectedPillarId(tactic.pillarId);
      setSelectedSubPillarId(tactic.goalId);
    } else if (!open) {
      form.resetFields();
      setTacticNameError(null);
      setSelectedPillarId(undefined);
      setSelectedSubPillarId(undefined);
    }
  }, [open, form, tactic]);

  if (open && !tactic) {
    return null;
  }

  return (
    <StyledModal
      title="Edit Tactic"
      open={open}
      onCancel={handleCancel}
      footer={null}
      centered
      closeIcon={<span>×</span>}
      width={600}
    >
      <FormContainer>
        <Form
          form={form}
          layout="vertical"
          requiredMark="optional"
          onValuesChange={changed => {
            if ('pillarId' in changed) {
              setSelectedPillarId(changed.pillarId);
            }
            if ('subPillarId' in changed) {
              setSelectedSubPillarId(changed.subPillarId);
            }
          }}
        >
          <FormItem
            label="Name"
            name="tacticName"
            rules={[
              { required: true, message: 'Please enter tactic name' },
              { whitespace: true, message: 'Tactic name cannot be empty' },
            ]}
          >
            <Input
              placeholder="User created"
              autoFocus
              onChange={handleTacticNameChange}
            />
          </FormItem>

          <FormItem
            label="Pillar"
            name="pillarId"
            rules={[{ required: true, message: 'Please select a pillar' }]}
          >
            <Select
              placeholder="Community"
              options={pillarsOptions}
              showSearch
              optionFilterProp="label"
              onChange={handlePillarChange}
            />
          </FormItem>

          <GoalLevelFields
            pillarsOfStrength={pillarsOfStrength}
            selectedPillarId={selectedPillarId}
            selectedSubPillarId={selectedSubPillarId}
            onGoalChange={handleSubPillarChange}
          />
        </Form>

        <ModalFooter>
          <CancelButton
            onClick={handleCancel}
            color="primary"
            disabled={isLoading}
          >
            Cancel
          </CancelButton>
          <PrimaryButton
            type="primary"
            onClick={handleSubmit}
            color="primary"
            loading={isLoading}
            disabled={pillarsOfStrengthError}
          >
            Save
          </PrimaryButton>
        </ModalFooter>
      </FormContainer>
    </StyledModal>
  );
};

export default EditTacticModal;
